import React from 'react';
import { OptImg } from '../../ui/OptImg';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import BackButton from '../../ui/BackButton';
import { CmsButton, CmsImage, SectionShell, SmartLink, videoEmbed, isDarkBg, SHADCN_BTN } from '../shared';

const isLightColor = (hex = '') => { const m = hex.replace('#', ''); if (m.length !== 6) return true; const [r, g, b] = [0, 2, 4].map((i) => parseInt(m.slice(i, i + 2), 16)); return (r * 299 + g * 587 + b * 114) / 1000 > 150; };

const Buttons = ({ p, light }) => (
  (p.primary_button?.text || p.secondary_button?.text || p.tertiary_button?.text) ? (
    <div className="flex flex-col sm:flex-row gap-4 mt-10">
      <CmsButton button={p.primary_button} variant={light ? 'light' : 'primary'} testId="hero-primary-btn" />
      <CmsButton button={p.secondary_button} variant={light ? 'outline_light' : 'secondary'} testId="hero-secondary-btn" />
      <CmsButton button={p.tertiary_button} variant={light ? 'outline_light' : 'secondary'} testId="hero-tertiary-btn" />
    </div>
  ) : null
);

const Copy = ({ p, light, center }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className={`max-w-3xl ${center ? 'mx-auto text-center' : ''}`}>
    {p.eyebrow && <span className={`label-overline ${light ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
    <h1 className={`mt-4 ${light ? 'text-white' : 'text-neutral-900'}`}>{p.title}</h1>
    {p.body && <p className={`mt-6 text-xl leading-relaxed whitespace-pre-line ${light ? 'text-neutral-200' : 'text-neutral-600'}`}>{p.body}</p>}
    <div className={center ? 'flex justify-center' : ''}><Buttons p={p} light={light} /></div>
  </motion.div>
);

export const HeroSection = ({ section, isFirst }) => {
  const p = section.props || {};
  const layout = section.layout || 'full_bg';
  const img = p.image?.url;

  if (layout === 'full_bg' && isFirst) {
    // Mirrors the coded HomePage hero: comma-split headline with accent second line, shadcn-sized buttons, scroll indicator.
    const t = p.title || '';
    const ii = t.lastIndexOf(' in ');
    const ci = ii > 0 ? ii : t.indexOf(',') + 1;
    const title = ci > 0 ? <>{t.slice(0, ci)}<br /><span className="text-amber-400">{t.slice(ci).trim()}</span></> : t;
    return (
      <section id={section.style?.anchor || section.id} data-testid="section-hero" data-section-id={section.id} className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          {img ? <OptImg src={img} alt={p.image?.alt_text || ''} className="w-full h-full object-cover" priority /> : <div className="w-full h-full bg-[color:var(--site-dark)]" />}
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 via-neutral-900/70 to-neutral-900/40" />
        </div>
        <div className="container-xl relative z-10 pt-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <h1 className="text-white mb-6">{title}</h1>
            {p.body && <p className="text-xl text-neutral-300 mb-10 max-w-2xl leading-relaxed whitespace-pre-line">{p.body}</p>}
            <div className="flex flex-col sm:flex-row gap-4">
              <CmsButton button={p.primary_button} className={`${SHADCN_BTN} !bg-white !text-neutral-900 hover:!bg-neutral-100 [&>svg]:ml-2`} testId="hero-primary-btn" />
              {p.secondary_button?.text && <SmartLink to={p.secondary_button.link || '#'} className={`${SHADCN_BTN} gap-2 text-sm font-medium tracking-wide uppercase !bg-amber-600 !text-white hover:!bg-amber-700`} data-testid="hero-secondary-btn"><Calendar className="w-4 h-4 mr-2" />{p.secondary_button.text}</SmartLink>}
              <CmsButton button={p.tertiary_button} className={`${SHADCN_BTN} !bg-transparent !text-white border border-white/30 hover:!bg-white hover:!text-neutral-900`} icon={false} testId="hero-tertiary-btn" />
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"><div className="w-1 h-2 bg-white/50 rounded-full" /></motion.div>
        </div>
      </section>
    );
  }

  if (layout === 'gradient_centered') {
    // Mirrors the coded PartnersPage hero: dark diagonal gradient, back link, pill eyebrow, accent word in the headline.
    return (
      <section id={section.style?.anchor || section.id} data-testid="section-hero" data-section-id={section.id} className="relative py-32 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20"><div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_25%,rgba(255,255,255,0.05)_50%,transparent_50%,transparent_75%,rgba(255,255,255,0.05)_75%)] bg-[length:60px_60px]" /></div>
        <div className="container-xl relative">
          <BackButton variant="light" />
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl mx-auto text-center mt-8">
            {p.eyebrow && <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-400 text-sm font-medium rounded-full mb-6">{p.eyebrow}</span>}
            <h1 className="!text-5xl md:!text-6xl !leading-none text-white mb-6">{p.title}{p.title_accent && <> <span className="text-amber-400">{p.title_accent}</span></>}</h1>
            {p.body && <p className="text-xl text-white/70 leading-relaxed whitespace-pre-line">{p.body}</p>}
          </motion.div>
        </div>
      </section>
    );
  }

  if (layout === 'full_bg' || layout === 'centered' || layout === 'video') {
    const vid = layout === 'video' ? videoEmbed(p.video_url) : null;
    return (
      <section id={section.style?.anchor || section.id} data-testid="section-hero" className={`relative ${isFirst ? 'min-h-screen' : 'min-h-[85vh]'} flex items-center text-white`}>
        <div className="absolute inset-0 overflow-hidden">
          {vid?.type === 'video' ? <video src={vid.src} autoPlay muted loop playsInline className="w-full h-full object-cover" poster={img} />
            : vid?.type === 'iframe' ? <iframe title="hero video" src={`${vid.src}&autoplay=1&mute=1&loop=1&controls=0`} className="w-full h-full scale-[1.4] pointer-events-none" allow="autoplay" />
            : img ? <OptImg src={img} alt={p.image?.alt_text || ''} className="w-full h-full object-cover" priority /> : <div className="w-full h-full bg-[color:var(--site-dark)]" />}
          <div className={`absolute inset-0 ${layout === 'centered' ? 'bg-neutral-900/60' : 'bg-gradient-to-r from-neutral-900/90 via-neutral-900/60 to-neutral-900/30'}`} />
        </div>
        <div className={`container-xl relative z-10 ${isFirst ? 'pt-24' : 'py-24'}`}><Copy p={p} light center={layout === 'centered'} /></div>
      </section>
    );
  }

  if (layout === 'compact_overlay') {
    const badges = (p.badges || []).filter((b) => b.text);
    return (
      <section id={section.style?.anchor || section.id} data-testid="section-hero" data-section-id={section.id} className="relative min-h-[320px] md:h-[40vh] py-10 md:py-0 flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {img && <OptImg src={img} alt={p.image?.alt_text || ''} className="w-full h-full object-cover" priority />}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-transparent" />
        </div>
        <div className={`container-xl relative z-10 ${isFirst ? 'pt-16' : ''}`}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl">
            {badges.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="hero-badges">
                {badges.map((b, i) => (
                  <SmartLink key={i} to={b.link} style={b.color ? { backgroundColor: b.color, color: isLightColor(b.color) ? '#171717' : '#ffffff', borderColor: b.color } : undefined}
                    className={`inline-block px-3 py-1 text-xs font-medium tracking-wider uppercase transition-colors ${i === 0 && !b.color ? 'bg-neutral-900 text-white hover:bg-neutral-700' : 'bg-white border border-neutral-300 text-neutral-800'}`}>{b.text}</SmartLink>
                ))}
              </div>
            )}
            {p.eyebrow && !badges.length && <span className="label-overline text-[color:var(--site-accent)]">{p.eyebrow}</span>}
            <h1 className="text-neutral-900 !text-3xl lg:!text-4xl !font-semibold !leading-tight">
              {p.title}{p.title_line2 && <><br /><span className="text-neutral-600">{p.title_line2}</span></>}
            </h1>
            {p.body && <p className="mt-3 text-base text-neutral-600 leading-relaxed">{p.body}</p>}
            <div className="mt-4 flex flex-wrap gap-3">
              <CmsButton button={p.primary_button} variant="primary" className="h-9 !px-5 !py-2 !text-sm !normal-case !tracking-normal !rounded-md shadow [&>svg]:ml-2" testId="hero-primary-btn" />
              {p.secondary_button?.text && <SmartLink to={p.secondary_button.link || '#'} className="inline-flex items-center justify-center gap-2 h-9 px-5 py-2 text-sm font-medium rounded-md border border-neutral-400 bg-white text-neutral-900 shadow-sm hover:bg-neutral-100 transition-colors" data-testid="hero-secondary-btn"><Calendar className="w-4 h-4 mr-2" />{p.secondary_button.text}</SmartLink>}
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (layout === 'split') {
    return (
      <SectionShell section={section} className={isFirst ? 'pt-28 lg:pt-32' : ''} testId="section-hero">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Copy p={p} />
          {img && <CmsImage image={p.image} className="w-full aspect-[4/3] object-cover" loading="eager" />}
        </div>
      </SectionShell>
    );
  }

  const style = { background: 'light', ...(section.style || {}) };
  const dark = isDarkBg(style);
  return (
    <SectionShell section={{ ...section, style }} className={isFirst ? (dark ? 'pt-28 lg:pt-32' : '!pt-32 !pb-20') : ''} testId="section-hero">
      <Copy p={p} light={dark} center={style.align === 'center'} />
    </SectionShell>
  );
};
